import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from '@/lib/toast'
import type {
  UsageResponse,
  CurrentPlanResponse,
  EmailStatusResponse,
  SubscriptionStatusResponse,
  BillingPortalResponse,
  ContentStrategyGenerateBody,
  ContentStrategyApplyBody,
  BusinessTypesResponse,
  PlatformsResponse,
  ContentTonesResponse
} from '@/types'

// Query keys
export const commonQueryKeys = {
  all: ['common'] as const,
  usage: () => [...commonQueryKeys.all, 'usage'] as const,
  currentPlan: () => [...commonQueryKeys.all, 'current-plan'] as const,
  availablePlans: () => [...commonQueryKeys.all, 'available-plans'] as const,
  emailStatus: () => [...commonQueryKeys.all, 'email-status'] as const,
  subscriptionStatus: () => [...commonQueryKeys.all, 'subscription-status'] as const,
  businessTypes: () => [...commonQueryKeys.all, 'business-types'] as const,
  platforms: () => [...commonQueryKeys.all, 'platforms'] as const,
  contentTones: () => [...commonQueryKeys.all, 'content-tones'] as const,
  languages: () => [...commonQueryKeys.all, 'languages'] as const,
  hcaptchaSiteKey: () => [...commonQueryKeys.all, 'hcaptcha-site-key'] as const,
  googleClientId: () => [...commonQueryKeys.all, 'google-client-id'] as const,
} as const

// Usage query
export const useUsage = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.usage(),
    queryFn: async () => {
      const response = await api.get<UsageResponse>('/user/usage')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Current plan query - scoped by userId to prevent cross-user cache issues
export const useCurrentPlan = (userId?: number, options = {}) => {
  return useQuery({
    queryKey: [...commonQueryKeys.currentPlan(), userId],
    queryFn: async () => {
      try {
        const response = await api.get('/plans/user/current')
        return response.data
      } catch (error: any) {
        // If endpoint doesn't exist (404), return null (no current plan)
        if (error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    staleTime: 0, // No staleness - always refetch to avoid showing previous user's plan
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    retry: false, // Don't retry on 404
    enabled: userId !== undefined, // Only run if userId is provided
    ...options,
  })
}

// Available plans query
export const useAvailablePlans = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.availablePlans(),
    queryFn: async () => {
      const response = await api.get('/plans/available')
      return response.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (plans don't change often)
    ...options,
  })
}

// Email status query
export const useEmailStatus = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.emailStatus(),
    queryFn: async () => {
      try {
        const response = await api.get<EmailStatusResponse>('/user/email-status')
        return response.data
      } catch (error: any) {
        // If endpoint doesn't exist (404), return default status
        if (error.response?.status === 404) {
          // console.log('ℹ️ Email status endpoint not available, using default (verified)')
          return { is_verified: true, email: '' }
        }
        throw error
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 404
    ...options,
  })
}

// Subscription status query - scoped by userId to prevent cross-user cache issues
export const useSubscriptionStatus = (userId?: number, options = {}) => {
  return useQuery({
    queryKey: [...commonQueryKeys.subscriptionStatus(), userId],
    queryFn: async () => {
      const response = await api.get<SubscriptionStatusResponse | null>('/api/payments/subscription-status')
      // Backend returns null (200) when no subscription exists, not 404
      return response.data
    },
    staleTime: 0, // No staleness - always refetch to avoid showing previous user's subscription
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    retry: false,
    enabled: userId !== undefined, // Only run if userId is provided
    ...options,
  })
}

// Plan upgrade mutation
export const usePlanUpgrade = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (planId: string) => {
      const response = await api.post('/user/upgrade-plan', { plan_id: planId })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.currentPlan() })
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() })
      toast.success('plan.upgradedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to upgrade plan')
    },
  })
}

// Alias for backward compatibility
export const useUpgradePlan = usePlanUpgrade

// Billing portal mutation
export const useBillingPortal = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<BillingPortalResponse>('/api/payments/create-portal-session')
      return response.data
    },
    onSuccess: (data) => {
      if (data.portal_url || data.url) {
        // Invalidate queries so they refetch when user returns from portal
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.currentPlan() })
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.subscriptionStatus() })
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() })
        window.location.href = data.portal_url || data.url
      } else {
        toast.error('plan.noPortalUrl')
      }
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to open billing portal')
    },
  })
}

// Alias for backward compatibility
export const useCreatePortalSession = useBillingPortal

// Content strategy generation mutation
export const useGenerateContentStrategy = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ContentStrategyGenerateBody) => {
      // Content strategy generation can take longer (AI processing), so use a longer timeout
      // Skip global error toast - component will handle errors with inline Alert
      const response = await api.post('/content-strategy/generate', data, {
        timeout: 60000, // 60 seconds - enough for AI content strategy generation
        skipGlobalErrorToast: true, // Component handles errors with Alert
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() })
    },
    // Removed onSuccess toast - let the component handle success messaging
    // Removed onError toast - let the component handle errors with Alert for better visibility
    // This prevents duplicate error messages (toast + Alert)
  })
}

// Content strategy application mutation
export const useApplyContentStrategy = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ContentStrategyApplyBody) => {
      const response = await api.post('/content-strategy/apply', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profiles'] })
      toast.success('contentStrategy.appliedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to apply content strategy')
    },
  })
}

// Business types query
export const useBusinessTypes = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.businessTypes(),
    queryFn: async () => {
      const response = await api.get<BusinessTypesResponse>('/lookups/business-types')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    ...options,
  })
}

// Platforms query
export const usePlatforms = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.platforms(),
    queryFn: async () => {
      const response = await api.get<PlatformsResponse>('/lookups/platforms')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    ...options,
  })
}

// All platforms query (for admin)
export const useAllPlatforms = (options = {}) => {
  return useQuery({
    queryKey: [...commonQueryKeys.platforms(), 'all'],
    queryFn: async () => {
      const response = await api.get<PlatformsResponse>('/lookups/platforms/all')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  })
}

// Content tones query
export const useContentTones = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.contentTones(),
    queryFn: async () => {
      const response = await api.get<ContentTonesResponse>('/lookups/content-tones')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    ...options,
  })
}

// Content purposes query
export const useContentPurposes = (options = {}) => {
  return useQuery({
    queryKey: [...commonQueryKeys.all, 'content-purposes'],
    queryFn: async () => {
      try {
        const response = await api.get('/lookups/content-purposes')
        return response.data
      } catch (error: any) {
        // If endpoint doesn't exist (404), return default purposes
        if (error.response?.status === 404) {
          return [
            'promote_product',
            'engage_audience',
            'share_news',
            'educational_content',
            'entertainment',
            'call_to_action'
          ]
        }
        throw error
      }
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    retry: false, // Don't retry on 404
    ...options,
  })
}

// hCaptcha site key query (public endpoint)
export const useHCaptchaSiteKey = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.hcaptchaSiteKey(),
    queryFn: async () => {
      const response = await api.get<{ site_key: string | null; enabled: boolean }>('/auth/hcaptcha-site-key')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    retry: false, // Don't retry if it fails (might not be configured)
    ...options,
  })
}

// Create checkout session mutation
export const useCreateCheckoutSession = () => {
  return useMutation({
    mutationFn: async (planId: number) => {
      const requestBody = { plan_id: planId };
      const response = await api.post('/api/payments/create-checkout-session', requestBody);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('plan.noCheckoutUrl');
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Failed to create checkout session';
      toast.raw.error(errorMessage);
    },
  })
}

// Reset usage mutation (admin only)
export const useResetUsage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessProfileId, resetCount, newLimit }: {
      businessProfileId: number;
      resetCount?: boolean;
      newLimit?: number
    }) => {
      const response = await api.post('/admin/usage/reset/' + businessProfileId, {
        reset_count: resetCount,
        new_limit: newLimit
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage'] })
      toast.success('admin.usageResetSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to reset usage')
    },
  })
}

// Languages query (for content generation language selection)
export const useLanguages = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.languages(),
    queryFn: async () => {
      const response = await api.get('/lookups/languages')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    ...options,
  })
}

// Google OAuth client ID query (public endpoint)
export const useGoogleClientId = (options = {}) => {
  return useQuery({
    queryKey: commonQueryKeys.googleClientId(),
    queryFn: async () => {
      const response = await api.get<{ client_id: string | null; enabled: boolean }>('/auth/google-client-id')
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
    retry: false, // Don't retry if it fails (might not be configured)
    ...options,
  })
}
